import {
	observable,
	action,
	autorun,
	reaction,
	computed,
	runInAction,
	IReactionDisposer,
} from "mobx";

import { BettingOfferManagementHub, HubState } from "@gp/hub";
import groupBy from "lodash/groupBy";
import each from "lodash/each";
import find from "lodash/find";
import isMatch from "lodash/isMatch";
import map from "lodash/map";
import uniqBy from "lodash/uniqBy";
import includes from "lodash/includes";
import unionBy from "lodash/unionBy";

import {
	BetSlipStore as GpBetSlipStore,
	EventType,
	BetSlipErrorCodes,
	parseSpecifier,
	BetSlipBetType,
	createEmptySlip,
	OddTypeStore,
	INotificationProviderError,
	INotificationProviderSuccess,
	OfferAdditionErrors,
	BetSlipErrorMessages,
} from "@gp/slip";

import {
	BetSlipTypes,
	BetSlipPaymentType,
	BetSlipOffer,
	Team,
} from "@gp/models";

import { getTip, playerSports, getType } from "@utils/offer";
import LoaderStore from "@state/stores/common/LoaderStore";
import { isUserAuthenticated, getCurrentCulture, DeviceDetector } from "@utils";
import { SessionStorageProvider, localizationService, redirect } from "@state";
import PlayedLiveEventsStore from "@state/stores/offer/live/PlayedLiveEventsStore";
import { DocumentExpirationErrorCodes } from "@api-types/documentExpiration/documentExpiration";
import { StorageStateKeysEnum } from "@utils/storage";
import { BetSlipInteractionTypeEnum } from "@data-types/analytics-tracker/BetSlipInteractionTypeEnum";
import { DateTime } from "luxon";
import { LoginResponseUser } from "@api-types/user/LoginResponseDto";
import { TBetSlipTab } from "@data-types/bet-slip";
import { AnnualIncomeErrorCodes } from "@api-types/user/AnnualIncomeErrorCodes";
import { createMultiOfferId } from "@gp/slip";
import AgencyUserApiService from "@api-services/user/AgencyUserApiService";
import { SlipApiService } from "@api-services/mvc/SlipApiService";
import BetSlipApiService from "@api-services/agency/BetSlipApiService";
import type { RootStore } from "@state/stores/types";
import { BetBuilderPick } from "@data-types/bet-booster/BetBuilderPicks";
import { THEME_SETTINGS } from "@theme-settings";
import { analyticsTracker } from "@utils/analytics";
import { BetPlacedMatchType } from "@data-types/analytics-tracker";
import { NotificationStore, UserAuthStore } from "@state/shared";
import { isAbortedRequestError } from "@utils/http/isAbortedRequestError";
import { WaitingPeriodPopupTypeEnum } from "@utils/common/waitingPeriodPopupTypeEnum";
import { findValidSubmitWallet } from "@utils/bet-slip/findValidSubmitWallet";
import { UserAccount } from "@data-types";
import { LugasAreaType } from "@data-types/user/LugasAreaType";
import { LugasErrorCodes } from "@data-types/user/LugasErrorCodes";
import { safeJsonParse } from "@utils/format/formatter";
import SavedSlipStore from "./SavedSlipStore";
import { BetSlipErrorCodesEnum } from "./BetSlipErrorCodesEnum";
import { Freebet, FreebetAction } from "@api-types/bet-slip/Freebet";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";

const stakeErrorCodes = [
	400017, 400018, 400019, 400020, 400024, 400030, 400032, 400033, 400036,
	400037, 400038,
];
const valueErrorCodesWithDetails = ["400065", "400068"];
const errorCodesWithoutDetails = ["400091", "400092"];

const keysList = [
	"paymentPerCombination",
	"gain",
	"totalStake",
	"payment",
	"stakePerCombination",
	"maxGain",
	"gainBonusAmount",
	"gainTaxAmount",
];

const AccountVerificationErrorCodes = {
	AccountVerification: 400185,
	AccountRecheck: 400535,
};

function getName(name: string, gender: undefined | number) {
	if (gender === 1) {
		return `${name} (${localizationService.t("OFFER.GENDER.WOMEN")})`;
	}

	return name;
}

type TBetSlipError = {
	message: (typeof OfferAdditionErrors | typeof BetSlipErrorMessages)[];
	errorCode: number;
	eventId: string;
	offer: BetSlipOffer;
};

/** slip store. */
export default class BetSlipStore {
	// ------------- COUNTDOWN -------------
	@observable public isCountdownVisible = false;
	@observable public countdownCounter = 0;
	@observable private countdownInterval: number | undefined = undefined;

	// ---------- Reuse ----------
	@observable public isReuseInProgress = false;

	// ---------- Modals ----------
	@observable public fullErrorMessage = "";
	@observable public showSubmitSuccessModal = false;

	// ---------- BetWidget ----------
	@observable activeBetTab: TBetSlipTab = TBetSlipTab.BetSlip;

	// ---------- Freebet ----------
	@observable public draftFreebetId: string | undefined | null = null;
	@observable public draftFreebetNumber: string | null = null;
	@observable public draftFreebetAmount: number = 0;
	@observable public availableFreeBets: Freebet[] = [];
	@observable public selectedFreebetNumber: string | null = null;
	@observable public selectedFreebetAmount: number = 0;
	@observable public freeBetsLoaded: boolean = false;
	@observable public isFetchingFreeBets: boolean = false;
	@observable public isFreebetModalOpen: boolean = false;

	public betSlipState: GpBetSlipStore;
	public playedLiveEventsStore: PlayedLiveEventsStore | undefined;

	@observable defaultCountdown: number;

	@computed public get isLoading() {
		return this.loader.isLoading;
	}

	@computed public get isLoadingProcess() {
		return this.loader.isLoadingProcess;
	}

	@observable public isSubmitDisabled = false;

	private rootStore: RootStore;
	private userStore: UserAuthStore;
	private offerHub: BettingOfferManagementHub;
	private notificationStore: NotificationStore;
	private loader: LoaderStore;

	public savedSlipStore: SavedSlipStore;

	constructor(params: {
		offerHub: BettingOfferManagementHub;
		notificationStore: NotificationStore;
		userStore: UserAuthStore;
		rootStore: RootStore;
		balanceResolver: InstanceType<typeof UserAuthStore>["resolveBalance"];
	}) {
		this.offerHub = params.offerHub;
		this.notificationStore = params.notificationStore;
		this.rootStore = params.rootStore;
		this.userStore = params.userStore;

		this.loader = new LoaderStore();
		this.savedSlipStore = new SavedSlipStore(this);

		const user = safeJsonParse<LoginResponseUser | null>(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.USER_KEY
			) as string,
			null
		);

		this.betSlipState = new GpBetSlipStore(
			BetSlipApiService,
			{
				hub: this.offerHub,
				isUserAuthenticated: isUserAuthenticated,
				notificationProvider: {
					onError: this.handleError,
					onSuccess: this.onSuccess,
				},
				oddTypeStore: new OddTypeStore(),

				updateBalance: () => {
					if (!this.betSlipState.hasErrors) {
						params.balanceResolver();
					} else {
						console.error("Error", this.betSlipState.error);
					}
				},
				addBetSlipRequestProperties:
					this.getBetSlipRequestOrgStructureInfo,
				slipIndicatorDuration: 2000,
				isReuseEnabled: true,
				actionControllerOptions: {
					actionThrottle: 400,
				},
			},
			{
				totalStake: user?.stakeAmount || THEME_SETTINGS.defaultStake,
			}
		);

		this.initSlipState();
	}

	/** Initialize slip state from the local storage. */
	@action.bound
	private initSlipState() {
		//#region read slip from storage

		let storageIsOneClickBet = Boolean(
			JSON.parse(SessionStorageProvider.get("ocb") as string)
		);

		if (storageIsOneClickBet) {
			this.betSlipState.betSlipBetType = BetSlipBetType.OneClickBet;
		}

		let freebet = safeJsonParse<Freebet | null>(
			SessionStorageProvider.get("freebet") as string,
			null
		);

		if (freebet) {
			try {
				if (freebet?.id) {
					this.betSlipState.setFreebetId(freebet.id);
					this.selectedFreebetNumber = freebet.freebetNumber;
					this.selectedFreebetAmount = freebet.stakeAmount ?? 0;
				} else {
					this.clearFreebetState();
				}
			} catch {
				this.clearFreebetState();
				SessionStorageProvider.remove("freebet");
			}
		} else {
			this.clearFreebetState();
		}

		let slip =
			JSON.parse(SessionStorageProvider.get("slip") as string) || {};

		if (slip == null || Object.entries(slip).length == 0) {
			this.betSlipState.slip = createEmptySlip();
			slip = Object.assign(
				{},
				this.betSlipState.slip,
				this.betSlipState.initialState
			);
		} else {
			this.betSlipState.slip = slip;
		}

		this.betSlipState.maxGain = slip.maxGain;
		this.betSlipState.gain = slip.gain;
		this.betSlipState.payment = slip.payment;
		this.betSlipState.stakePerCombination = slip.stakePerCombination;
		this.betSlipState.paymentPerCombination = slip.paymentPerCombination;
		this.betSlipState.totalStake = slip.stake;
		this.betSlipState.combinations = slip.combinations;

		this.betSlipState.handlingFeeAmount = slip.handlingFee || 0;
		this.betSlipState.handlingFeePercentage =
			slip.handlingFeePercentage || 0;
		this.betSlipState.gainBonusAmount = slip.gainBonus || 0;
		this.betSlipState.gainBonusPercentage = slip.gainBonusPercentage || 0;
		this.betSlipState.gainTaxAmount = slip.gainTax || 0;
		this.betSlipState.gainTaxPercentage = slip.gainTaxPercentage || 0;

		this.betSlipState.eventCount = slip.eventCount || 0;

		//#endregion read slip from storage

		this.betSlipState.betSlipType =
			JSON.parse(SessionStorageProvider.get("type") as string) ||
			BetSlipTypes.COMBINATION;

		this.betSlipState.paymentType =
			(JSON.parse(
				sessionStorage.getItem("payment_type") as string
			) as BetSlipPaymentType | null) || BetSlipPaymentType.TOTAL;

		if (this.betSlipState.betSlipType === BetSlipTypes.SYSTEM) {
			this.betSlipState.availableCombinations = JSON.parse(
				SessionStorageProvider.get("system") as string
			);
			this.betSlipState.selectedCombinations = JSON.parse(
				SessionStorageProvider.get("ssystem") as string
			);
		}

		this.betSlipState.clickedTips =
			JSON.parse(SessionStorageProvider.get("clicked-tips") as string) ||
			[];

		this.betSlipState.acceptOddsChanges =
			JSON.parse(
				SessionStorageProvider.get(
					"betslip-accept-odds-change"
				) as string
			) || false;

		this.betSlipState.bankEvents = [
			...new Set(
				this.betSlipState.slip.betSlipOffers
					.filter((offer: BetSlipOffer) => offer.isBank)
					.map((offer: BetSlipOffer) => offer.eventId)
			),
		];
	}

	//#region slip submit

	@observable public showConfirmBetSlipSubmit = false;

	@action.bound
	public cancelBetSlipSubmit() {
		this.showConfirmBetSlipSubmit = false;
	}

	@action.bound
	public startBetSlipSubmitProcess() {
		if (isUserAuthenticated()) {
			this.showConfirmBetSlipSubmit = true;
		} else {
			window.location.href = `/${getCurrentCulture()}/auth/login`;
		}
	}

	@action.bound
	public async onSubmitSlip(shouldReplaceHistory?: boolean) {
		this.loader.suspend();
		this.isSubmitDisabled = true;
		this.showConfirmBetSlipSubmit = false;

		if (this.betSlipState.slip.isLive) {
			this.startCountdown();
		}

		// needs to happen because of possible wrong data formatting in acc key in localStorage
		// !!!! - can be removed after some time but doesn't hurt
		await this.rootStore.userAuthStore.checkBalanceEntry();

		const currentAccount: UserAccount | null = findValidSubmitWallet(
			this.rootStore.userAuthStore
		);

		if (
			!currentAccount ||
			(this.isOneClickBetActive &&
				this.rootStore.waitingPeriodStore.timeLeft > 0)
		) {
			return;
		}
		const usedFreebetId = this.betSlipState.freebetId;
		const referenceId = this.rootStore.createReferenceId();
		await this.betSlipState.submitSlip(
			false,
			false,
			false,
			currentAccount.accountTypeId,
			referenceId
		);

		runInAction(() => {
			if (!this.betSlipState.hasErrors) {
				if (
					!this.betSlipState.subscription ||
					!this.betSlipState.subscription.events
				) {
					//@ts-expect-error
					this.rootStore?.liveMenuViewStore?.liveEventsFromMyBets?.updateEvents(
						this.betSlipState.subscription.events
					);
				}

				let matches: BetPlacedMatchType[] = [];

				this.betSlipState.slip.betSlipOffers.forEach((offer) => {
					const t1: Team | undefined =
						this.betSlipState.lookupStore.teams.get(
							offer.sportData.teamOneId
						);
					const t2: Team | undefined =
						this.betSlipState.lookupStore.teams.get(
							offer.sportData.teamTwoId
						);

					const tournament =
						this.betSlipState.lookupStore.tournaments.get(
							//@ts-expect-error
							offer.sportData.tournamentId
						);

					let matchObject: BetPlacedMatchType = {
						sport_abrv: offer.sportData.sportAbrv || "",
						tournament_abrv: tournament?.abrv || "",
						team_one_abrv: t1?.abrv || "",
						team_two_abrv: t2?.abrv || "",
						betting_type_abrv:
							offer.sportData.bettingTypeAbrv || "",
						event_id: offer.eventId,
					};

					if (this.rootStore.platform === "web") {
						this.rootStore.myBetsViewStore.syncEventBetsTab(
							offer.eventId
						);
					}
					matches.push(matchObject);
				});

				analyticsTracker.trackBetPlaced(
					this.betSlipType,
					this.betSlipBetType,
					this.betSlipState.payment,
					this.betSlipState.slip.id,
					matches,
					this.betSlipState.maxGain
				);

				if (DeviceDetector.isMobileTheme) {
					redirect(
						`/${getCurrentCulture()}/bet-slip/bet-slip-receipt/${
							this.betSlipState?.slip?.id
						}/${currentAccount?.id}`,
						shouldReplaceHistory
					);
				}
				this.betSlipState.reset({ resetSubmitDelay: false });
				this.rootStore.betBuilderStore.clearAdditionalOfferStores();

				if (usedFreebetId) {
					this.resetDraftFreebet();
					this.refreshFreeBets();
				}

				this.rootStore.waitingPeriodStore.resetWaitingPeriodDuration();
			}

			this.loader.resume();
			this.isSubmitDisabled = false;
		});
	}

	@action.bound
	public async addRemoveOffer(
		tip: Parameters<
			(typeof this)["betSlipState"]["addRemoveOfferWithTypeChange"]
		>[1]
	) {
		if (this.betSlipState.isSubmitting) {
			return;
		}

		if (!this.betSlipState.freebetId && this.betSlipState.isSlipEmpty) {
			this.resetDraftFreebet();
		}

		if (this.betSlipState.isOneClickBetActive) {
			this.loader.suspend();
			if (tip.multiOffers && tip.multiOffers.length) {
				this.switchBetTab(TBetSlipTab.BetSlip);
			}
		}

		if (
			this.betSlipState.isOneClickBetActive &&
			(this.betSlipState.oneClickBetAmount || 0) <= 0
		) {
			this.notificationStore.error(
				localizationService.t("BET_SLIP.ERRORS.VALUE_LESS_THAN_ONE")
			);
			return;
		}

		if (this.betSlipState.isOneClickBetActive) {
			this.betSlipState.setSubmitDelay(this.defaultCountdown);
			if (tip.isLive) {
				this.startCountdown();
			}

			if (this.playedLiveEventsStore != null) {
				this.playedLiveEventsStore.addPlayedOneClickTip(tip);
			}
		}

		const currentAccountType = findValidSubmitWallet(
			this.userStore
		)?.accountTypeId;

		const errors: TBetSlipError[] =
			await this.betSlipState.addRemoveOfferWithTypeChange(
				tip,
				currentAccountType,
				false,
				this.betSlipState.isOneClickBetActive
					? this.rootStore.createReferenceId()
					: ""
			);

		this.addRemoveErrorHandler(errors, tip, currentAccountType);

		this.switchBetTab(TBetSlipTab.BetSlip);
		if (this.betSlipState.isOneClickBetActive) {
			this.loader.resume();
		}
	}

	@action.bound
	addRemoveErrorHandler(errors: any, tip: any, currentAccountType: any) {
		if (errors && errors.length) {
			const error = errors[0];

			switch (error.errorCode) {
				case 0: //attempt to add multiOffer to slip while slip type is "System"
				case 6: //attempt to add BetBuilder to slip while slip contains multi-offers offer
					this.rootStore.betBuilderPopupStore.activateBetBuilderPopup(
						localizationService.t(
							`TICKET.ERRORS.${error && error.message}`
						),
						async () => {
							this.onResetSlip();

							const event = {
								id: tip.eventId,
								liveStatus: tip.isLive ? 1 : 0,
							};

							if (tip.multiOffers.length > 1) {
								await this.rootStore.betBuilderStore.updatePresetsQuotaAndSetAvailableOffers(
									tip,
									event
								);
							} else {
								await this.rootStore.betBuilderStore.updateQuotaAndSetAvailableOffers(
									tip.multiOffers[0],
									event
								);
							}

							this.rootStore.betBuilderPopupStore.deactivateBetBuilderPopup();
						}
					);
					return true;
				case 5: //multioffer betslip eg 1x2 for same event that are not bet builder
					this.rootStore.betBuilderPopupStore.activateBetBuilderPopup(
						localizationService.t(
							`TICKET.ERRORS.${error && error.message}`
						),
						async () => {
							this.onResetSlip();
							await this.betSlipState.addRemoveOfferWithTypeChange(
								tip,
								currentAccountType,
								true
							);

							this.rootStore.betBuilderPopupStore.deactivateBetBuilderPopup();
						}
					);
					return true;
				case 1: // Attempt to add multiOffer while slip alread has multiOffer for that event //no longer possible
				case 2: // Attempt to add multioffer while there is a multiOffer already on slip and "multipleMultiOffersOnSameSlip" is set to false
					this.rootStore.betBuilderPopupStore.activateBetBuilderPopup(
						localizationService.t(
							`TICKET.ERRORS.${error && error.message}`
						),
						async () => {
							errors.forEach((err: any) => {
								this.betSlipState.slip.betSlipOffers =
									this.betSlipState.slip.betSlipOffers.filter(
										(bso: BetSlipOffer) =>
											bso.eventId !== err.eventId
									);

								this.rootStore.betBuilderStore.clearAdditionalOfferStoreByEventId(
									err.eventId
								);
							});

							const event = {
								id: tip.eventId,
								liveStatus: tip.isLive ? 1 : 0,
							};

							if (tip.multiOffers.length > 1) {
								await this.rootStore.betBuilderStore.updatePresetsQuotaAndSetAvailableOffers(
									tip,
									event
								);
							} else {
								await this.rootStore.betBuilderStore.updateQuotaAndSetAvailableOffers(
									tip.multiOffers[0],
									event
								);
							}

							this.rootStore.betBuilderPopupStore.deactivateBetBuilderPopup();
						}
					);
					return true;
				case 3: // Attempt to add regular offer to betSlip with multiOffer offer for same event already on slip
					this.rootStore.betBuilderPopupStore.activateBetBuilderPopup(
						localizationService.t(
							`TICKET.ERRORS.${error && error.message}`
						),
						async () => {
							errors.forEach((err: any) => {
								this.betSlipState.slip.betSlipOffers =
									this.betSlipState.slip.betSlipOffers.filter(
										(bso: BetSlipOffer) =>
											bso.eventId !== err.eventId
									);
								this.rootStore.betBuilderStore.clearAdditionalOfferStoreByEventId(
									err.eventId
								);
							});

							await this.betSlipState.addRemoveOfferWithTypeChange(
								tip,
								currentAccountType,
								true
							);

							this.rootStore.betBuilderPopupStore.deactivateBetBuilderPopup();
						}
					);
					return true;

				case 4: // Attempt to add multioffer offer to betSlip with regular offer for same event already on slip
					this.rootStore.betBuilderPopupStore.activateBetBuilderPopup(
						localizationService.t(
							`TICKET.ERRORS.${error && error.message}`
						),
						async () => {
							errors.forEach((err: any) => {
								this.betSlipState.slip.betSlipOffers =
									this.betSlipState.slip.betSlipOffers.filter(
										(bso: BetSlipOffer) =>
											bso.eventId !== err.eventId
									);
								this.betSlipState.clickedTips =
									this.betSlipState.clickedTips.filter(
										(ct) => ct.eventId !== err.eventId
									);
							});

							const event = {
								id: tip.eventId,
								liveStatus: tip.isLive ? 1 : 0,
							};

							if (tip.multiOffers.length > 1) {
								await this.rootStore.betBuilderStore.updatePresetsQuotaAndSetAvailableOffers(
									tip,
									event
								);
							} else {
								await this.rootStore.betBuilderStore.updateQuotaAndSetAvailableOffers(
									tip.multiOffers[0],
									event
								);
							}

							this.rootStore.betBuilderPopupStore.deactivateBetBuilderPopup();
						}
					);
					return;

				default:
					console.error("Use slip unknown error", error);
					return true;
			}
		}
	}

	@action.bound
	private startCountdown() {
		this.countdownCounter = this.countDownValue;
		if (this.countdownCounter !== 0) {
			this.isCountdownVisible = true;
			this.countdownInterval = Number(
				setInterval(async () => {
					--this.countdownCounter;
					if (this.countdownCounter === 0) {
						clearInterval(this.countdownInterval);
						this.isCountdownVisible = false;
					}
				}, 1000)
			);
		} else {
			this.isCountdownVisible = false;
		}
	}

	@action.bound
	/** Toggle is offer in slip ignored for submit and validate. */
	public async toggleTipExclude(id: string) {
		await this.betSlipState.toggleTipExclude(id);
		SessionStorageProvider.set(
			"clicked-tips",
			JSON.stringify(this.betSlipState.clickedTips)
		);
	}

	//#endregion slip submit

	//#region freebet slip submit

	@action.bound
	async fetchFreeBets(force = false) {
		if (!this.rootStore.userAuthStore.isLoggedIn) return;
		if (this.isFetchingFreeBets) return;
		if (this.freeBetsLoaded && !force) return;

		this.isFetchingFreeBets = true;

		try {
			const response = await BetSlipApiService.getAvailableFreebets();
			this.availableFreeBets = response ?? [];
			this.freeBetsLoaded = true;
		} catch (error) {
			console.error("Failed to fetch freebets", error);
			this.availableFreeBets = [];
			this.freeBetsLoaded = false;
		} finally {
			this.isFetchingFreeBets = false;
		}
	}

	@action.bound
	async refreshFreeBets() {
		this.freeBetsLoaded = false;
		await this.fetchFreeBets(true);
	}

	@action.bound
	async openFreebetSelector() {
		if (!this.canOpenFreebetModal) {
			this.notificationStore.error(
				localizationService.t(
					"BET_SLIP.FREEBET_REMOVE_COMBINATIONS_INFO"
				)
			);
			return;
		}

		await this.fetchFreeBets();

		if (!this.hasAvailableFreeBets) return;

		this.isFreebetModalOpen = true;
	}

	@action.bound
	closeFreebetModal() {
		this.resetDraftFreebet();
		this.isFreebetModalOpen = false;
	}

	@action.bound
	selectDraftFreebet(freebet: Freebet) {
		this.draftFreebetId = freebet.id;
		this.draftFreebetNumber = freebet.freebetNumber;
		this.draftFreebetAmount = freebet.stakeAmount;
	}

	@action.bound
	applyFreebet() {
		if (!this.draftFreebetId) return;

		this.setFreebetOption(
			this.draftFreebetId,
			this.draftFreebetAmount,
			this.draftFreebetNumber ?? null
		);

		this.onFreebetTotalStakeChange(
			this.draftFreebetAmount,
			this.draftFreebetNumber ?? null
		);

		this.onTotalStakeApply();

		this.closeFreebetModal();
	}

	@action.bound
	removeFreebet() {
		this.betSlipState.shouldApplyTotalStake = true;
		this.setFreebetOption(null, 0, null);
		this.setFreebetDefaultStakeAmount();
		this.resetDraftFreebet();
	}

	@action.bound
	handleCombinationConflict() {
		this.notificationStore.error(
			localizationService.t("BET_SLIP.REMOVED_FREEBET_STAKE")
		);
		this.removeFreebet();
	}

	@action.bound
	resetDraftFreebet() {
		this.draftFreebetId = this.betSlipState.freebetId;
		this.draftFreebetNumber = this.selectedFreebetNumber;
		this.draftFreebetAmount = this.selectedFreebetAmount;
	}

	@action.bound
	clearFreebetState() {
		this.betSlipState.setFreebetId(undefined);
		this.selectedFreebetNumber = null;
		this.setFreebetDefaultStakeAmount();
	}

	@action.bound
	setFreebetOption(
		freebetId: string | null | undefined,
		amount: number,
		number: string | null
	) {
		this.betSlipState.setFreebetId(freebetId);
		this.selectedFreebetAmount = amount;
		this.selectedFreebetNumber = number;
	}

	@action.bound
	resetFreebets() {
		this.availableFreeBets = [];
		this.freeBetsLoaded = false;
	}

	@action.bound
	clearFreebetOnLogout() {
		SessionStorageProvider.remove("freebet");
		this.removeFreebet();
		this.resetFreebets();
	}

	@action.bound
	public setFreebetDefaultStakeAmount() {
		if (this.rootStore.userAuthStore.user !== null) {
			this.onTotalStakeChange(
				this.rootStore.userAuthStore.user?.stakeAmount
			);
			this.onTotalStakeApply();
		} else {
			this.onTotalStakeChange(THEME_SETTINGS.defaultStake);
			this.onTotalStakeApply();
		}
	}

	@action.bound
	public onFreebetTotalStakeChange(
		amount: number,
		freebetNumber: string | null = null
	) {
		this.selectedFreebetNumber = freebetNumber;
		this.onTotalStakeChange(amount);
	}

	@action.bound
	public async freebetConfirmation(
		freebetId: string,
		actionType: FreebetAction
	) {
		try {
			await BetSlipApiService.freebetConfirmation(freebetId, actionType);
		} catch (err: any) {
			const error = err?.data ?? err;
			if (error && error?.errorCode === 401300) {
				this.notificationStore.info(error?.errorMessage);
				redirect(`/${getCurrentCulture()}/${DefaultPath.HOME}`);
				return;
			}

			if (error && error?.errorCode === 401301) {
				this.notificationStore.error(error?.errorMessage);
				redirect(`/${getCurrentCulture()}/${DefaultPath.HOME}`);
				return;
			}

			if (error && error?.errorCode === 401302) {
				this.notificationStore.error(error?.errorMessage);
				redirect(`/${getCurrentCulture()}/${DefaultPath.HOME}`);
				return;
			}

			if (error && error?.errorCode === 401307) {
				this.notificationStore.error(error?.errorMessage);
				redirect(`/${getCurrentCulture()}/${DefaultPath.HOME}`);
				return;
			}
		}
	}

	//#endregion freebet slip submit

	//#region clear all form bet slip

	@observable public showResetModal = false;

	@action.bound
	public startBetSlipReset() {
		this.showResetModal = true;
	}

	@action.bound
	public cancelBetSlipReset() {
		this.showResetModal = false;
	}

	@action.bound
	public acceptBetSlipReset() {
		this.rootStore.waitingPeriodStore.hidePopup();

		this.showResetModal = false;
		this.onResetSlip();
	}

	//#endregion clear all from bet slip

	//#region change bet slip type

	@observable public showOneClickBetClearSlipModal = false;

	@action.bound
	public acceptSlipClearAndSetToOneClick() {
		this.rootStore.waitingPeriodStore.hidePopup();

		this.betSlipState.onBetSlipBetTypeChange(BetSlipBetType.OneClickBet);
		if (!this.betSlipState.acceptOddsChanges && this.isOneClickBetActive) {
			this.betSlipState.onToggleAcceptOddsChangesChange();
		}
		runInAction(() => {
			this.cancelBetSlipSubmit();
			this.showOneClickBetClearSlipModal = false;
		});
	}

	@action.bound
	onBetSlipBetTypeChangeAccept() {
		this.betSlipState.onBetSlipBetTypeChange(BetSlipBetType.OneClickBet);
		this.showOneClickBetClearSlipModal = false;
	}

	@action.bound
	onCloseOneClickBetChangeModal() {
		this.showOneClickBetClearSlipModal = false;
	}

	//#endregion change bet slip type

	//#region computed and getters

	@computed private get countDownValue(): number {
		return this.betSlipState.submitDelay || 0;
	}

	@computed public get isOneClickBetActive(): boolean {
		return this.betSlipState.isOneClickBetActive;
	}

	@computed public get isSlipEmpty(): boolean {
		return this.betSlipState.isSlipEmpty;
	}

	@computed public get betSlipType(): "single" | "combination" | "system" {
		return this.betSlipState.betSlipType;
	}

	@computed public get betSlipBetType(): "one-click" | "normal" {
		return this.betSlipState.betSlipBetType;
	}

	@computed
	public get displaySlipEventEntries(): {
		[key: string]: GpBetSlipStore["slipOffer"];
	} {
		return this.betSlipState.displaySlipEntries;
	}

	@computed public get currentSlipOffers() {
		return this.betSlipState.currentSlipOffers;
	}

	@computed public get slipSize(): number {
		return this.betSlipState.slipSize;
	}

	@computed public get combinations() {
		return this.betSlipState.combinations;
	}

	@computed public get isProcessingSlipEmpty(): boolean {
		return this.betSlipState.isProcessingSlipEmpty;
	}

	@computed public get paymentPerCombination(): number {
		return this.betSlipState.paymentPerCombination;
	}

	@computed public get payment(): number {
		return this.betSlipState.payment;
	}

	@computed public get availableCombinations(): {
		key: number;
		numberOfCombinations: number;
		total: number;
	}[] {
		return this.betSlipState.availableCombinations;
	}

	@computed public get bankOfferCount(): number {
		return this.betSlipState.bankOfferCount;
	}

	@computed public get eventCount(): number {
		return this.betSlipState.eventCount;
	}

	@computed public get isHandlingFeeActive(): boolean {
		return this.betSlipState.isHandlingFeeActive;
	}

	@computed public get handlingFeePercentage(): number {
		return this.betSlipState.handlingFeePercentage;
	}

	@computed public get handlingFeeAmount(): number {
		return this.betSlipState.handlingFeeAmount;
	}

	@computed public get slipMaxCoefficient(): number {
		return this.betSlipState.slipMaxCoefficient;
	}

	@computed public get gainBonusAmount(): number {
		return this.betSlipState.gainBonusAmount;
	}

	@computed public get gainBonusPercentage(): number {
		return this.betSlipState.gainBonusPercentage;
	}

	@computed public get gainTaxPercentage(): number {
		return this.betSlipState.gainTaxPercentage;
	}

	@computed public get gainTaxAmount(): number {
		return this.betSlipState.gainTaxAmount;
	}

	@computed public get maxGain(): number {
		return this.betSlipState.maxGain;
	}

	@computed public get gain(): number {
		return this.betSlipState.gain;
	}

	@computed public get acceptOddsChanges() {
		return this.betSlipState.acceptOddsChanges;
	}

	public get isSystemEnabled(): boolean {
		return this.betSlipState.isSystemDisabled;
	}

	@computed public get oneClickBetAmount(): number {
		return this.betSlipState.oneClickBetAmount;
	}

	@computed public get invalidBetsCount(): number {
		return this.betSlipState.offers.filter((bso: BetSlipOffer) => {
			if (bso.multiOffers && bso.multiOffers.length) {
				return bso.multiOffers.some(
					(offer: BetSlipOffer) =>
						(offer.isEventLocked ||
							offer.isKeyLocked ||
							offer.isOfferLocked ||
							offer.isDeleted) &&
						offer.errors != null
				);
			}
			return (
				(bso.isEventLocked ||
					bso.isKeyLocked ||
					bso.isOfferLocked ||
					bso.isDeleted) &&
				bso.errors != null
			);
		}).length;
	}

	@computed public get hasInvalidBets(): boolean {
		return this.invalidBetsCount > 0;
	}

	@computed public get isSubmitting(): boolean {
		return this.betSlipState.isSubmitting;
	}

	@computed
	get hasAvailableFreeBets() {
		return this.availableFreeBets.length > 0;
	}

	@computed
	get hasLongTermOffer() {
		return this.betSlipState.slip.betSlipOffers.some(
			(offer) => offer.sportData.eventType === 2
		);
	}

	@computed
	get canOpenFreebetModal() {
		return (
			!this.isSlipEmpty &&
			this.combinations <= 1 &&
			!this.hasLongTermOffer
		);
	}

	@computed
	get shouldShowFreebetButton(): boolean {
		return (
			this.rootStore.userAuthStore.isLoggedIn &&
			this.betSlipState.betSlipType !== BetSlipTypes.SYSTEM &&
			!this.isSlipEmpty &&
			this.hasAvailableFreeBets &&
			!this.hasLongTermOffer
		);
	}

	//#endregion computed and getters

	//#region actions

	@action.bound
	public setEventSwitcherEventIds(eventId: string) {
		if (
			!this.betSlipState.subscription ||
			!this.betSlipState.subscription.events
		) {
			return;
		}
		const list = this.betSlipState.subscription.events;
		const eventIndex = list.indexOf(eventId);

		this.rootStore.eventSwitcherViewStore.setEventList(list, eventIndex);
	}

	@action.bound
	public async onInitialize() {
		this.startReactions();
		this.betSlipState.onInitialize();

		if (this.isReuseInProgress) {
			this.isReuseInProgress = false;
			return;
		}

		try {
			await this.betSlipState.processCurrentBetSlipState();

			const response = await SlipApiService.delay();
			this.betSlipState.setSubmitDelay(response ? response.value : 0);
			this.defaultCountdown = response ? response.value : 0;
		} catch (err) {
			console.error(err);
			this.betSlipState.setSubmitDelay(0);
		}
	}

	@action.bound
	public async useBetSlip(
		sharedSlip: any,
		stake?: number,
		options?: { trackAsReuse?: boolean }
	) {
		runInAction(() => {
			this.isReuseInProgress = true;
		});

		const betSlip = sharedSlip;
		if (stake) {
			betSlip.payment = stake;
		} else {
			betSlip.payment =
				this.betSlipState.payment ||
				this.rootStore.userAuthStore.user?.stakeAmount ||
				THEME_SETTINGS.defaultStake;
		}

		const errors: TBetSlipError[] =
			this.betSlipState.canAppendBetSlipOffer(betSlip);
		if (errors && errors.length) {
			const error = errors[0];

			switch (error.errorCode) {
				case 0: //attempt to add multiOffer to slip while slip type is "System"
				case 5: //multioffer betslip eg 1x2 for same event that are not bet builder
				case 6: //attempt to add BetBuilder to slip while slip contains multi-offers offer
					this.rootStore.betBuilderPopupStore.activateBetBuilderPopup(
						localizationService.t(
							`TICKET.ERRORS.${error && error.message}`
						),
						async () => {
							this.onResetSlip();

							await this.useBetSlip(betSlip, undefined, options);
							this.rootStore.betBuilderPopupStore.deactivateBetBuilderPopup();
						}
					);
					return;

				case 1: // Attempt to add multiOffer while slip alread has multiOffer for that event
				case 2: // Attempt to add multioffer while there is a multiOffer already on slip and "multipleMultiOffersOnSameSlip" is set to false
				case 3: // Attempt to add regular offer to betSlip with multiOffer offer for same event already on slip
				case 4: // Attempt to add multioffer offer to betSlip with regular offer for same event already on slip
					this.rootStore.betBuilderPopupStore.activateBetBuilderPopup(
						localizationService.t(
							`TICKET.ERRORS.${error && error.message}`
						),
						async () => {
							errors.forEach((err) => {
								this.betSlipState.slip.betSlipOffers =
									this.betSlipState.slip.betSlipOffers.filter(
										(bso: BetSlipOffer) =>
											bso.eventId !== err.eventId
									);
								this.betSlipState.clickedTips =
									this.betSlipState.clickedTips.filter(
										(ct) => ct.eventId !== err.eventId
									);
							});
							await this.useBetSlip(betSlip, undefined, options);
							this.rootStore.betBuilderPopupStore.deactivateBetBuilderPopup();
						}
					);
					return;

				default:
					console.error("Use slip unknown error", error);
					return;
			}
		}

		if (
			betSlip.betSlipType?.name &&
			betSlip.betSlipType.name.toLowerCase() === "multi offer bet slip"
		) {
			betSlip.betSlipType.name = BetSlipTypes.COMBINATION;
		}
		this.betSlipState.onBetSlipBetTypeChange(BetSlipBetType.Normal);
		// display tip comes unformatted from API, we need to format it (pre:outcometext from API)
		betSlip.betSlipOffers.forEach(
			// @ts-expect-error
			(offer) => (offer.displayTip = getTip(offer))
		);
		const offersMap = new Map();

		this.betSlipState.slip.betSlipOffers.forEach((offer) => {
			offersMap.set(offer.bettingOfferId, {
				...offer,
				multiOffers: [...(offer.multiOffers || [])],
			});
		});

		betSlip.betSlipOffers.forEach((offer: any) => {
			if (offersMap.has(offer.bettingOfferId)) {
				const existingOffer = offersMap.get(offer.bettingOfferId);
				offersMap.set(offer.bettingOfferId, {
					...existingOffer,
					multiOffers: unionBy(
						[...(existingOffer.multiOffers || [])],
						[...(offer.multiOffers || [])],
						"bettingOfferId"
					),
				});
			} else {
				offersMap.set(offer.bettingOfferId, {
					...offer,
					multiOffers: [...(offer.multiOffers || [])],
				});
			}
		});

		betSlip.betSlipOffers = Array.from(offersMap.values());

		if (betSlip !== null) {
			runInAction(async () => {
				this.switchBetTab(TBetSlipTab.BetSlip);

				await this.betSlipState.useExistingSlip(betSlip, {
					acceptOddsChanges: true,
					useValidOffersOnly: false,
					paymentType: betSlip.betSlipOffers.some(
						// @ts-expect-error
						(element, index) => {
							return (
								betSlip.betSlipOffers.findIndex(
									// @ts-expect-error
									(item) => item.eventId === element.eventId
								) !== index
							);
						}
					)
						? "PerCombination"
						: "Total",
				});
			});

			if (options?.trackAsReuse) {
				analyticsTracker.trackBetSlipInteraction(
					BetSlipInteractionTypeEnum.Reuse
				);
			}
		}
	}

	@action.bound
	public setDefaultBetSlipStake(value: number | null = null) {
		try {
			const newValue = value ? value : this.betSlipState.payment;
			AgencyUserApiService.saveNewDefaultStake(newValue);

			if (this.userStore.user != null) {
				this.userStore.user.stakeAmount = newValue;
				this.rootStore.localStorageProvider.set(
					StorageStateKeysEnum.USER_KEY,
					JSON.stringify(this.userStore.user)
				);
				this.betSlipState.shouldApplyTotalStake = true;
				this.onTotalStakeChange(newValue);
				this.onTotalStakeApply();
			}
		} catch (error) {
			console.error(error);
		}
		this.notificationStore.success(
			localizationService.t("BET_SLIP.DEFAULT_STAKE_MESSAGE", {
				stake: value ? value : this.betSlipState.payment,
				currency: this.rootStore.userAuthStore.currency,
			})
		);
	}

	@action.bound
	public changeBetSlipBetType(value: this["betSlipBetType"]) {
		if (
			value === BetSlipBetType.OneClickBet &&
			this.betSlipState.isSlipEmpty
		) {
			this.betSlipState.onBetSlipBetTypeChange(value);
			return;
		} else if (
			value === BetSlipBetType.OneClickBet &&
			!this.betSlipState.isSlipEmpty
		) {
			this.showOneClickBetClearSlipModal = true;
			return;
		}

		this.betSlipState.onBetSlipBetTypeChange(value);
	}

	@action.bound
	public onBetSlipTypeChange(value: "single" | "combination" | "system") {
		this.betSlipState.onBetSlipTypeChange(value);
	}

	@action.bound
	public deactivateOneClickBet() {
		this.betSlipState.onDeactivateOneClickBet();
	}

	@action.bound
	public switchBetTab(value: typeof this.activeBetTab) {
		this.activeBetTab = value;
	}

	//#endregion actions

	//#region handlers

	@action.bound
	private onSuccess(m: INotificationProviderSuccess) {
		this.notificationStore.success(
			localizationService.t("BET_SLIP.SUBMIT_SUCCESS")
		);
		const type = this.betSlipState.isOneClickBetActive
			? BetSlipInteractionTypeEnum.OneClickBet
			: BetSlipInteractionTypeEnum.Submit;
		analyticsTracker.trackBetSlipInteraction(type);

		if (this.isCountdownVisible) {
			this.isCountdownVisible = false;
		}

		//#region inject newly submitted events into played events counter

		if (this.playedLiveEventsStore != null) {
			let eventIds = [];

			if (this.betSlipState.isOneClickBetActive) {
				this.playedLiveEventsStore.successfulOneClickTips(
					this.betSlipState._oneClickTips
				);
			} else {
				eventIds = this.betSlipState.slip.betSlipOffers.reduce<
					string[]
				>((acc: string[], offer: BetSlipOffer) => {
					acc.push(offer.eventId);
					return acc;
				}, []);
				this.playedLiveEventsStore.updateEvents(eventIds);
			}
		}

		//#endregion inject newly submitted events into played events counter

		if (this.rootStore.platform == "web") {
			this.rootStore.myBetsViewStore.refresh();
		}
	}

	/**
	 * Handles bet slip error notification
	 */
	@action.bound
	private async handleError(rawError: INotificationProviderError) {
		// clear interval for submitting live ticket
		clearInterval(this.countdownInterval);
		this.isCountdownVisible = false;

		if (rawError == null) {
			return;
		}

		if (rawError.error && isAbortedRequestError(rawError.error)) {
			return;
		}

		if (
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode ===
				BetSlipErrorCodesEnum.BetSlipAlreadySubmitted
		) {
			if (!rawError.referenceId) {
				return;
			}

			if (DeviceDetector.isMobileTheme) {
				await this.getTicketByReferenceId(
					rawError.referenceId,
					rawError.bettingAccountTypeId || ""
				);
			}
			this.betSlipState.reset({ resetSubmitDelay: false });
			this.rootStore.betBuilderStore.clearAdditionalOfferStores();

			this.rootStore.waitingPeriodStore.resetWaitingPeriodDuration();
			return;
		}
		if (
			rawError &&
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode === LugasErrorCodes.LugasAreaUnchanged
		) {
			this.rootStore.notificationStore.error(
				localizationService.t("error-handling:LUGAS_AREA_UNCHANGED")
			);
			this.rootStore.realityCheckStore.activatePopup();
			return;
		}

		if (
			rawError &&
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode === LugasErrorCodes.LugasDown
		) {
			this.rootStore.notificationStore.error(
				localizationService.t("MEMBERSHIP.ERR_HANDLING.LUGAS_DOWN")
			);
			return;
		}

		if (
			rawError &&
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode ===
				LugasErrorCodes.LugasPlayerInWaitingPeriod
		) {
			this.rootStore.waitingPeriodStore.hidePopup();

			this.rootStore.waitingPeriodStore.showPopup(
				WaitingPeriodPopupTypeEnum.SUBMIT
			);
			return;
		}

		if (
			rawError &&
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode == 400213
		) {
			this.rootStore.userAuthStore.userDataConfirmationStore.showPopup();
			return;
		}

		if (
			rawError &&
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode === LugasErrorCodes.LugasOasisDown
		) {
			this.rootStore.notificationStore.error(
				localizationService.t("error-handling:OASIS_DOWN")
			);
			return;
		}

		if (
			rawError &&
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode === LugasErrorCodes.LugasGracePeriod
		) {
			this.rootStore.gracePeriodStore.showPopup();
			return;
		}

		if (
			rawError &&
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode ===
				LugasErrorCodes.LugasAreaGamblingPreventionInfoRequired
		) {
			this.rootStore.areaChangeStore.showPopup(
				LugasAreaType.SportBetting
			);
			this.rootStore.retryHandlerStore.setRetry(
				() => this.onSubmitSlip(),
				true
			);
			return;
		}

		if (
			rawError.error &&
			!Array.isArray(rawError.error) &&
			rawError.error.statusCode === 401
		) {
			//the user is logged out on some way and needs to be redirected to login page
			window.location.href = `/${getCurrentCulture()}/auth/login?isSessionTerminated=true`;
		}

		if (
			rawError.error &&
			!Array.isArray(rawError.error) &&
			rawError.error.errorCode === 400007
		) {
			this.notificationStore.error(rawError.error.details);
			return;
		}

		if (
			rawError.error &&
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode === 400981
		) {
			this.removeFreebet();
			await this.refreshFreeBets();
			this.handleCombinationConflict();
			return;
		}

		if (
			rawError.error &&
			Array.isArray(rawError.error) &&
			(rawError.error[0]?.errorCode === 400982 ||
				rawError.error[0]?.errorCode === 400988 ||
				rawError.error[0]?.errorCode === 400984 ||
				rawError.error[0]?.errorCode === 400987)
		) {
			this.removeFreebet();
			await this.refreshFreeBets();
			this.notificationStore.error(rawError.error[0]?.errorMessage);
			return;
		}

		if (rawError.mappedError != null) {
			this.handleMappedError(rawError);
		} else if (rawError.statusCode === BetSlipErrorCodes.Generic) {
			this.notificationStore.error(rawError.message);
		} else {
			this.notificationStore.error(
				localizationService.t(rawError.message)
			);
		}
	}

	@action.bound
	private handleMappedError(rawError: INotificationProviderError) {
		if (!Array.isArray(rawError.mappedError)) {
			// @ts-expect-error by TS this will never happen, mappedError will always be array
			if (rawError.mappedError?.message != null) {
				// @ts-expect-error
				this.notificationStore.error(rawError.mappedError.message);
			}
			return;
		}

		const verificationError = rawError.mappedError.filter(
			(err) =>
				err.errorCode ===
					AccountVerificationErrorCodes.AccountVerification ||
				err.errorCode ===
					AccountVerificationErrorCodes.AccountRecheck ||
				err.errorCode ===
					DocumentExpirationErrorCodes.UserDocumentExpirationDateExpired ||
				err.errorCode ===
					DocumentExpirationErrorCodes.UserDocumentExpirationDateNotSet ||
				err.errorCode ===
					AnnualIncomeErrorCodes.AnnualIncomeUpdateRequired
		);
		if (verificationError.length) {
			let link: string | null = null;
			let titleText: string | null = null;
			if (
				verificationError[0].errorCode ==
				AccountVerificationErrorCodes.AccountVerification
			) {
				link = "account-settings/account-verification";
				titleText = "ACCOUNT_VERIFICATION.TITLE";
			}
			if (
				verificationError[0].errorCode ==
				AccountVerificationErrorCodes.AccountRecheck
			) {
				link = "user-profile-recheck";
				titleText = "ACCOUNT_RECHECK.TITLE";
			}
			if (
				verificationError[0].errorCode ==
				DocumentExpirationErrorCodes.UserDocumentExpirationDateExpired
			) {
				link = "account-settings/document-expiration";
				titleText = "DOCUMENT_EXPIRATION.TITLE";
			}
			if (
				verificationError[0].errorCode ==
				DocumentExpirationErrorCodes.UserDocumentExpirationDateNotSet
			) {
				link = "account-settings/document-expiration";
				titleText = "DOCUMENT_EXPIRATION.TITLE";
			}
			if (
				verificationError[0].errorCode ==
				AnnualIncomeErrorCodes.AnnualIncomeUpdateRequired
			) {
				link = "account-settings/personal-data";
				titleText = "COMMON.NAVIGATION.PERSONAL_DATA";
			}
			this.rootStore.accountVerificationStore.showPopup(
				link as string, // TODO what if null?
				titleText as string, // TODO what if null?
				verificationError[0].errorMessage
			);
			return;
		}

		if (rawError.userAccountDeactivationGracePeriodExpirationDate) {
			this.rootStore.userAuthStore.setWalletGracePeriod(
				rawError.userAccountDeactivationGracePeriodExpirationDate
			);
		}

		if (
			rawError &&
			Array.isArray(rawError.error) &&
			rawError.error[0]?.errorCode ==
				BetSlipErrorCodesEnum.WalletActivationGracePeriod
		) {
			this.rootStore.walletGracePeriodPopupStore.setMessage(
				rawError.error[0].errorMessage
			);
			this.rootStore.walletGracePeriodPopupStore.showPopup(
				rawError.userAccountDeactivationGracePeriodExpirationDate
			);
			return;
		}
		if (!this.betSlipState.isSubmitting) {
			rawError.mappedError = rawError.mappedError.filter(
				(errorItem) =>
					errorItem.errorCode !== 400062 &&
					errorItem.errorCode !== 400063
			);
		}

		each(rawError.mappedError, (errorItem) => {
			if (errorItem.errorMessage.includes("{currency}")) {
				errorItem.errorMessage = errorItem.errorMessage.replaceAll(
					"{currency}",
					this.rootStore.userAuthStore.currency
				);
			}

			if (errorItem.errorCode === 400262) {
				errorItem.errorMessage = errorItem.errorMessage.replace(
					"{0}",
					//@ts-expect-error
					DateTime.fromISO(errorItem.previouslyClosedAt).toFormat("f")
				);
				errorItem.errorMessage = errorItem.errorMessage.replace(
					"{1}",
					//@ts-expect-error
					DateTime.fromISO(errorItem.nextOpening).toFormat("f")
				);
			}

			if (
				errorItem.errorMessage.includes("{0}") &&
				errorItem.sportData != null
			) {
				const currentSportData = errorItem.sportData[0];
				if (errorItem.errorCode === 400091) {
					const eventName = currentSportData.sportData.eventName
						? currentSportData.sportData.eventName
						: `${currentSportData.sportData.teamOneName} - ${currentSportData.sportData.teamTwoName}`;
					errorItem.errorMessage = `<p class="slip-error">${errorItem.errorMessage.replace(
						"{0}",
						eventName
					)}</p>`;
				} else if (errorItem.errorCode === 400092) {
					const bettingType = parseSpecifier(
						currentSportData.sportData
							.bettingTypeNameForBettingSlip,
						currentSportData.specifier
							? currentSportData.specifier
							: {}
					);

					errorItem.errorMessage = `<p class="slip-error">${errorItem.errorMessage.replace(
						"{0}",
						bettingType.parsedString
					)}</p>`;
				} else if (errorItem.errorCode === 400098) {
					errorItem.errorMessage = errorItem.errorMessage.replace(
						"{0}",
						errorItem.totalNumberOfOffersPerBetSlip || "0"
					);
				}
			}
		});
		const errorsByErrorCode = groupBy(rawError.mappedError, "errorCode");
		let errorMessages = Object.keys(errorsByErrorCode);
		each(errorMessages, (key) => {
			//key is error code
			const containsSportData =
				find(
					errorsByErrorCode[key],
					(item) =>
						item.sportData != null && item.sportData.length > 0
				) !== undefined;

			if (
				containsSportData == null ||
				errorCodesWithoutDetails.includes(key.toString())
			) {
				return;
			}

			var errorMessageContainsSuggestedValue = find(
				errorsByErrorCode[key],
				(item) => /\d/.test(item.errorMessage)
			);

			if (
				errorMessageContainsSuggestedValue != null &&
				!valueErrorCodesWithDetails.includes(key.toString())
			) {
				return;
			}

			const currentMessageList = errorsByErrorCode[key];
			const eventIdNotExists = currentMessageList.some((o) => !o.eventId);
			const groupedCurrentMessageList = groupBy(
				currentMessageList,
				eventIdNotExists ? "sportData[0].eventId" : "eventId"
			);
			// @ts-expect-error
			let currentMessageDetailsList = [];
			each(Object.values(groupedCurrentMessageList), (message) => {
				let details = {
					bettingType: "",
					teams: "",
				};
				const sportDataList = message[0].sportData;

				if (sportDataList == null || sportDataList.length <= 0) {
					return;
				}
				let sportData = sportDataList[0].sportData;
				if (key === "400039") {
					const offer = message[0];
					const bettingTypeOneFromResponse = offer.bettingTypeOne;
					const bettingTypeTwoFromResponse = offer.bettingTypeTwo;
					let bettingTypeOneFormatted: BetSlipOffer | string | null =
						null;
					let bettingTypeTwoFormatted: BetSlipOffer | string | null =
						null;

					if (bettingTypeOneFromResponse?.includes("{")) {
						this.betSlipState.slip.betSlipOffers.forEach((o) => {
							if (bettingTypeOneFormatted) {
								return;
							}
							if (o.bettingOfferId === offer.correlationId) {
								bettingTypeOneFormatted = o;
								return;
							}
							if (o.multiOffers && o.multiOffers.length > 0) {
								const multiOffer = o.multiOffers.find(
									(mo) =>
										mo.bettingOfferId == offer.correlationId
								);
								if (multiOffer) {
									bettingTypeOneFormatted = multiOffer;
									Object.assign(o.sportData, {
										bettingTypeNameForBettingSlip:
											multiOffer.sportData
												.bettingTypeNameForBettingSlip,
										bettingTypeAbrv:
											multiOffer.sportData
												.bettingTypeAbrv,
									});
								}
							}
						});

						if (
							bettingTypeOneFormatted &&
							"sportData" in bettingTypeOneFormatted
						) {
							bettingTypeOneFormatted = getType(
								bettingTypeOneFormatted
							);
						}
					} else {
						bettingTypeOneFormatted =
							bettingTypeOneFromResponse || "";
					}

					if (bettingTypeTwoFromResponse?.includes("{")) {
						this.betSlipState.slip.betSlipOffers.forEach((o) => {
							if (bettingTypeTwoFormatted) {
								return;
							}
							if (o.bettingOfferId === offer.correlationTwoId) {
								bettingTypeTwoFormatted = o;
								return;
							}
							if (o.multiOffers && o.multiOffers.length > 0) {
								const multiOffer = o.multiOffers.find(
									(mo) =>
										mo.bettingOfferId ==
										offer.correlationTwoId
								);
								if (multiOffer) {
									bettingTypeTwoFormatted = multiOffer;
									Object.assign(o.sportData, {
										bettingTypeNameForBettingSlip:
											multiOffer.sportData
												.bettingTypeNameForBettingSlip,
										bettingTypeAbrv:
											multiOffer.sportData
												.bettingTypeAbrv,
									});
								}
							}
						});
						if (
							bettingTypeTwoFormatted &&
							"sportData" in bettingTypeTwoFormatted
						) {
							bettingTypeTwoFormatted = getType(
								bettingTypeTwoFormatted
							);
						}
					} else {
						bettingTypeTwoFormatted =
							bettingTypeTwoFromResponse || "";
					}

					details = {
						teams: "",
						bettingType: `${bettingTypeOneFormatted}\n${bettingTypeTwoFormatted}`,
					};
				} else if (key === "400043") {
					const secondTournament =
						this.betSlipState.slip.betSlipOffers.find(
							(offer: BetSlipOffer) =>
								offer.eventId === message[0].eventTwoId
						);
					let secondTournamentName = null;
					if (secondTournament) {
						secondTournamentName =
							secondTournament.sportData.tournamentName;
					}
					details = {
						teams: `${sportData.tournamentName}${
							secondTournamentName
								? `  ${secondTournamentName}`
								: ""
						}`,
						bettingType: "",
					};
				} else if (key === "400040" || key === "400041") {
					const eventOne = this.betSlipState.slip.betSlipOffers.find(
						(offer: BetSlipOffer) =>
							offer.eventId === message[0].eventId
					);
					const eventTwo = this.betSlipState.slip.betSlipOffers.find(
						(offer: BetSlipOffer) =>
							offer.eventId === message[0].eventTwoId
					);
					let eventOneName = null;
					let eventTwoName = null;

					if (eventOne && eventOne.sportData) {
						eventOneName = eventOne.sportData.eventName
							? eventOne.sportData.eventName
							: `${eventOne.sportData.teamOneName} - ${eventOne.sportData.teamTwoName}`;
					}
					if (eventTwo && eventTwo.sportData) {
						eventTwoName = eventTwo.sportData.eventName
							? eventTwo.sportData.eventName
							: `${eventTwo.sportData.teamOneName} - ${eventTwo.sportData.teamTwoName}`;
					}

					details = {
						teams:
							eventOneName && eventTwoName
								? `${eventOneName} ${eventTwoName}`
								: "",
						bettingType: "",
					};
				} else if (key === "400044") {
					details = {
						teams: sportData.tournamentName,
						bettingType: "",
					};
				} else {
					const homeTeamName =
						sportData.eventType === EventType.Normal
							? getName(
									sportData.teamOneName,
									sportData.teamOneGender
								)
							: "";
					const awayTeamName =
						sportData.eventType === EventType.Normal
							? getName(
									sportData.teamTwoName,
									sportData.teamTwoGender
								)
							: "";

					details = {
						teams:
							sportData.eventType === EventType.Normal
								? `${homeTeamName} - ${awayTeamName}`
								: `${sportData.eventName}`,
						bettingType: "",
					};
				}
				if (
					![
						"400039",
						"400040",
						"400041",
						"400043",
						"400044",
					].includes(key.toString())
				) {
					each(message, (o, idx) => {
						if (o.sportData == null) {
							return;
						}
						if (
							o.sportData[0].multiOffers &&
							o.sportData[0].multiOffers.length
						) {
							return;
						}
						const currentSportData = o.sportData[0];

						let c1Key = "OFFER.COMPETITORS.HOME";
						let c2Key = "OFFER.COMPETITORS.AWAY";
						if (
							playerSports.includes(
								currentSportData.sportData.sportAbrv || ""
							)
						) {
							c1Key = "OFFER.COMPETITORS.PLAYER1";
							c2Key = "OFFER.COMPETITORS.PLAYER2";
						}

						const bettingType = parseSpecifier(
							currentSportData.sportData
								.bettingTypeNameForBettingSlip,
							currentSportData.specifier
								? currentSportData.specifier
								: {},
							{
								competitor1: localizationService.t(c1Key),
								competitor2: localizationService.t(c2Key),
							}
						);

						let tip = currentSportData.bettingTypeTip;
						if (currentSportData.playerId != null) {
							tip = `${currentSportData.playerFirstName} ${currentSportData.playerLastName}`;
						} else if (
							currentSportData.teamId != null &&
							currentSportData.teamName != null
						) {
							tip = currentSportData.teamName;
						} else if (currentSportData.displayTip != null) {
							tip = currentSportData.displayTip;
						}

						const stringComposition = `${bettingType.parsedString} ${tip}`;
						if (idx === 0) {
							details.bettingType += stringComposition;
						} else {
							details.bettingType += `\n${stringComposition}`;
						}
					});
				}
				const existingDetails = find(
					// @ts-expect-error
					currentMessageDetailsList,
					(o) => {
						return isMatch(o, details);
					}
				);
				if (!existingDetails) {
					currentMessageDetailsList.push(details);
				}
			});

			if (currentMessageDetailsList.length > 0) {
				each(currentMessageList, (errorItem) => {
					each(
						// @ts-expect-error
						currentMessageDetailsList,
						(detailObj) => {
							errorItem.errorMessage += `<p class="slip-error">${
								detailObj.teams
							}${
								key === "400065"
									? ` : ${parseInt(
											errorItem.suggestedValue || "0"
										)}`
									: ""
							}</p>${
								key === "400065"
									? ""
									: `<p class="bet-stop">${detailObj.bettingType}</p>`
							}`;
						}
					);
				});
			}
		});

		// If errors contain stake error, reset stake to last valid
		const stakeErrorCodes = [
			400017, 400018, 400019, 400020, 400024, 400030, 400032, 400033,
			400036, 400037, 400038, 400064,
		];
		const hasStakeErrors = rawError.mappedError.some((i) =>
			stakeErrorCodes.includes(i.errorCode || -1)
		);
		if (hasStakeErrors && this.betSlipState.isChangingSlipValues) {
			this.betSlipState.totalStake = this.betSlipState._memory.totalStake;
			this.betSlipState.stakePerCombination =
				this.betSlipState._memory.stakePerCombination;
			this.betSlipState.paymentPerCombination =
				this.betSlipState._memory.paymentPerCombination;
			this.betSlipState.gain = this.betSlipState._memory.gain;
			this.betSlipState.payment = this.betSlipState._memory.payment;
			this.betSlipState.maxGain = this.betSlipState._memory.maxGain;
			this.betSlipState.gainBonusPercentage =
				this.betSlipState._memory.gainBonusPercentage;
			this.betSlipState.gainBonusAmount =
				this.betSlipState._memory.gainBonusAmount;
			this.betSlipState.gainTaxPercentage =
				this.betSlipState._memory.gainTaxPercentage;
			this.betSlipState.gainTaxAmount =
				this.betSlipState._memory.gainTaxAmount;
			this.betSlipState.handlingFeePercentage =
				this.betSlipState._memory.handlingFeePercentage;
			this.betSlipState.handlingFeeAmount =
				this.betSlipState._memory.handlingFeeAmount;
			this.betSlipState.combinationBettingOffersCount =
				this.betSlipState._memory.combinationBettingOffersCount;
		}

		const errorMessage = map(
			uniqBy(rawError.mappedError, (o) => o.errorMessage),
			"errorMessage"
		).join("\n");
		if (errorMessage !== "") {
			this.notificationStore.error(errorMessage);
		}
	}

	//#endregion handlers

	//#region is methods

	public isTipSelected = (tipId: string): boolean => {
		if (this.betSlipState.isOneClickBetActive) {
			return this.betSlipState.isInOneClickBet(tipId);
		}
		return this.betSlipState.isTipClicked(tipId);
	};

	public isEventBank = (eventId: string): boolean => {
		return this.betSlipState.bankEvents.indexOf(eventId) > -1;
	};

	public isCombinationSelected = (key: number): boolean => {
		return this.betSlipState.selectedCombinations.indexOf(key) > -1;
	};

	//#endregion is methods

	//#region add/remove methods

	@action.bound
	public addRemoveBank(eventId: string) {
		this.betSlipState.addRemoveBank(eventId);
	}

	@action.bound
	public addRemoveCombination(comboKey: number) {
		this.betSlipState.paymentType = BetSlipPaymentType.PER_COMBINATION;
		this.betSlipState.addRemoveCombination(comboKey);

		if (this.betSlipState.freebetId && this.combinations > 1) {
			this.handleCombinationConflict();
		}
	}

	@action.bound
	public async removeInactiveBets() {
		this.betSlipState.removeInactiveOffers();
		if (!this.betSlipState.freebetId) {
			this.resetDraftFreebet();
		}
	}

	@action.bound
	public removeFromBetSlip(tip: {
		id: string;
		multiOffers?: { multiOfferId: string }[];
	}) {
		this.betSlipState.removeFromBetSlip(tip);
		if (!this.betSlipState.freebetId) {
			this.resetDraftFreebet();
		}
	}

	//do not use outside of BB presets
	@action.bound
	public async forcefullyRemovePresetMultiOfferOffer(
		multiOfferPreset: BetBuilderPick
	) {
		try {
			const tip = {
				id: createMultiOfferId(multiOfferPreset),
				multiOffers: multiOfferPreset.multiOffers.map((mo) => ({
					multiOfferId: mo.bettingOfferId,
				})),
			};
			await this.betSlipState.removeFromBetSlip(tip);
		} catch (error) {
			console.error(error);
		}
	}

	//#endregion add/remove methods

	//#region on action methods

	@action.bound
	public async onGainApply() {
		await this.betSlipState.onGainApply();
		this.checkBetSlipValues();
	}

	@action.bound
	public async checkLengthValue(e: Event) {
		// @ts-expect-error
		if (e.target.value.length > 16) {
			// @ts-expect-error
			e.target.value = e.target.value.substr(0, 16);
		}
	}

	@action.bound
	public async onPaymentPerCombinationApply() {
		await this.betSlipState.onPaymentPerCombinationApply();
		this.checkBetSlipValues();
	}

	@action.bound
	public onPaymentPerCombinationChange(value: number) {
		if (isNaN(value) || value <= 0) {
			this.notificationStore.error(
				localizationService.t("BET_SLIP.ERRORS.VALUE_LESS_THAN_ONE")
			);
			return;
		}
		this.betSlipState.onPaymentPerCombinationChange(value);
	}

	@action.bound
	public onTotalStakeChange(value: number) {
		if (isNaN(value) || value <= 0) {
			this.notificationStore.error(
				localizationService.t("BET_SLIP.ERRORS.VALUE_LESS_THAN_ONE")
			);
			return;
		}
		this.betSlipState.onTotalStakeChange(value);
	}

	@action.bound
	public async onTotalStakeApply() {
		await this.betSlipState.onTotalStakeApply();
		this.checkBetSlipValues();
	}

	@action.bound
	public onGainChange(value: number) {
		if (isNaN(value) || value < 0) {
			this.notificationStore.error(
				localizationService.t("BET_SLIP.ERRORS.VALUE_LESS_THAN_ONE")
			);
			return;
		}
		this.betSlipState.onGainChange(value);
	}

	@action.bound
	public onToggleAcceptOddsChangesChange() {
		this.betSlipState.onToggleAcceptOddsChangesChange();
	}

	@action.bound
	public onOneClickBetAmountChange(value: number) {
		if (this.betSlipState.isOneClickBetActive && value < 0) {
			this.notificationStore.error(
				localizationService.t("BET_SLIP.ERRORS.VALUE_LESS_THAN_ONE")
			);
		}

		this.betSlipState.onOneClickBetAmountChange(value);
	}

	@action.bound
	public onDeactivateOneClickBet() {
		this.betSlipState.onDeactivateOneClickBet();
	}

	@action.bound
	public onSwitchFromOneClickBet(value: BetSlipTypes) {
		this.betSlipState.betSlipBetType = BetSlipBetType.Normal;
		this.betSlipState.betSlipType = value;
	}

	@action.bound
	async getTicketByReferenceId(
		referenceId: string,
		bettingAccountTypeId?: string
	) {
		const accountTypeId = findValidSubmitWallet(this.userStore);
		try {
			const response =
				await BetSlipApiService.getSlipByReference(referenceId);
			let betSlipId = response.id;

			redirect(
				`/${getCurrentCulture()}/bet-slip/bet-slip-receipt/${
					betSlipId
				}/${accountTypeId?.id}`,
				true
			);
		} catch (error) {
			console.error(error);
		}
	}

	//#endregion on action methods

	//#region helpers

	private checkBetSlipValues = (): void => {
		if (
			this.betSlipState.error &&
			this.betSlipState.error.error &&
			Array.isArray(this.betSlipState.error.error)
		) {
			const containsStakeError =
				find(this.betSlipState.error.error, (errorItem) =>
					includes(stakeErrorCodes, errorItem.errorCode)
				) !== undefined;

			if (containsStakeError) {
				each(
					keysList,
					(
						key: keyof GpBetSlipStore &
							keyof GpBetSlipStore["_memory"]
					) => {
						this.betSlipState[key] = this.betSlipState._memory[key];
					}
				);
			} else {
				each(
					keysList,
					(
						key: keyof GpBetSlipStore &
							keyof GpBetSlipStore["_memory"]
					) => {
						this.betSlipState._memory[key] = this.betSlipState[key];
					}
				);
			}
		}
	};

	getBetSlipRequestOrgStructureInfo = () => {
		if (
			!this.rootStore.userAuthStore.userAccounts ||
			!this.rootStore.userAuthStore.isLoggedIn
		) {
			return null;
		}
		let selectedWallet = findValidSubmitWallet(
			this.rootStore.userAuthStore
		);

		if (!selectedWallet) {
			console.error(
				"Unable to find activeWalletId",
				this.rootStore.userAuthStore.activeWalletId
			);
			return null;
		}

		return {
			betSlipRequestOrgStructureInfo: {
				shopId: selectedWallet.shopId,
				agencyId: selectedWallet.agencyId,
			},
		};
	};
	//#endregion helpers

	//#region sessionStorageSync

	private storeReactions: IReactionDisposer[] = [];

	@action.bound
	private startReactions() {
		this.stopStoreReactions();

		// prettier-ignore
		this.storeReactions.push(reaction(
			() => this.userStore.user?.stakeAmount || THEME_SETTINGS.defaultStake,
			(newStake) => {
				runInAction(() => {
					this.betSlipState.initialState.payment = newStake;
					this.betSlipState.initialState.totalStake = newStake;
					this.betSlipState.initialState.paymentPerCombination = newStake;

					if (this.betSlipState.eventCount === 0) {
						this.betSlipState.payment = newStake;
						this.betSlipState.paymentPerCombination = newStake;
						this.betSlipState.totalStake = newStake;
					}
				})
			}, {
			fireImmediately: true
		}
		));

		// prettier-ignore
		this.storeReactions.push(reaction(
			() =>
				!this.isSlipEmpty &&
				this.offerHub?.connection?.state === HubState.NOT_INITIALIZED,
			(isSlipWaitingForOfferHub) => {
				if (isSlipWaitingForOfferHub) {
					this.rootStore.offerHub.startSession();
				}
			},
			{ fireImmediately: true }
		));

		// prettier-ignore
		this.storeReactions.push(autorun(() => {
			if (this.betSlipState.slipSize === 1) {
				var lastOffer = this.betSlipState.slip.betSlipOffers[0];
				const isExcluded = lastOffer.isExcluded;
				if (isExcluded) {
					this.betSlipState.toggleTipExclude(
						lastOffer.bettingOfferId
					);
				}
			}
		}));

		// Bring the persisted betting type names up to the current language once the lookups arrive.
		// Without this the copy written below stays in whatever language it was first received in, and
		// every consumer still reading `sportData` (see `getType`) keeps serving it.
		// prettier-ignore
		this.storeReactions.push(autorun(() => {
			const bettingTypes = this.betSlipState.lookupStore.bettingTypes;
			const teams = this.betSlipState.lookupStore.teams;
			const offers = this.betSlipState.slip?.betSlipOffers;

			if (bettingTypes.size === 0 || !offers?.length) return;

			runInAction(() => {
				const repair = (offer: {
					bettingTypeId: string;
					sportData?: {
						bettingTypeNameForBettingSlip: string;
						teamOneId: string;
						teamOneName: string;
						teamTwoId: string;
						teamTwoName: string;
					} | null;
				}) => {
					const name = bettingTypes.get(offer.bettingTypeId)
						?.nameForBettingSlip;

					if (
						name &&
						offer.sportData &&
						offer.sportData.bettingTypeNameForBettingSlip !== name
					) {
						offer.sportData.bettingTypeNameForBettingSlip = name;
					}

					if (offer.sportData) {
						offer.sportData.teamOneName = teams.get(offer.sportData.teamOneId)?.name ?? offer.sportData.teamOneName;
						offer.sportData.teamTwoName = teams.get(offer.sportData.teamTwoId)?.name ?? offer.sportData.teamTwoName;
					}
				};

				offers.forEach((offer) => {
					repair(offer);
					offer.multiOffers?.forEach(repair);
				});
			});
		}));

		// prettier-ignore
		this.storeReactions.push(autorun(() => {
			if (
				this.isSlipEmpty &&
				this.betSlipBetType !== BetSlipBetType.OneClickBet &&
				!this.isOneClickBetActive
			) {
				SessionStorageProvider.set("slip", "{}");
			} else {
				SessionStorageProvider.set(
					"slip",
					JSON.stringify(this.betSlipState.slip)
				);
			}
		}));

		// prettier-ignore
		this.storeReactions.push(autorun(() => {
			SessionStorageProvider.set(
				"type",
				JSON.stringify(this.betSlipState.betSlipType)
			);
		}));

		this.storeReactions.push(
			autorun(() => {
				sessionStorage.setItem(
					"payment_type",
					JSON.stringify(this.betSlipState.paymentType)
				);
			})
		);
		// prettier-ignore
		this.storeReactions.push(autorun(() => {
			if (this.betSlipState.availableCombinations == null) {
				SessionStorageProvider.set("system", JSON.stringify([]));
			} else {
				SessionStorageProvider.set(
					"system",
					JSON.stringify(this.betSlipState.availableCombinations)
				);
			}
		}));

		// prettier-ignore
		this.storeReactions.push(reaction(
            () => ({
                freebetId: this.betSlipState.freebetId,
                number: this.selectedFreebetNumber,
                amount: this.selectedFreebetAmount,
                isLoggedIn: this.rootStore.userAuthStore.isLoggedIn
            }),
            (data) => {
                if (!data.isLoggedIn) {
                    runInAction(() => {
                        SessionStorageProvider.remove("freebet");
                        this.betSlipState.freebetId = undefined;
                        this.selectedFreebetNumber = null;
                        this.selectedFreebetAmount = 0;
                        this.resetDraftFreebet();
                        this.onTotalStakeChange(THEME_SETTINGS.defaultStake);
                        this.onTotalStakeApply();
                    });
                    return;
                }

                if (!data.freebetId) {
                    SessionStorageProvider.remove("freebet");
                    return;
                }

                SessionStorageProvider.set(
                    "freebet",
                    JSON.stringify({
                        id: data.freebetId,
                        freebetNumber: data.number,
                        stakeAmount: data.amount
                    })
                );
            }
        ));

		// prettier-ignore
		this.storeReactions.push(autorun(() => {
			if (this.betSlipState.selectedCombinations == null) {
				SessionStorageProvider.set("ssystem", JSON.stringify([]));
			} else {
				SessionStorageProvider.set(
					"ssystem",
					JSON.stringify(this.betSlipState.selectedCombinations)
				);
			}
		}));

		// prettier-ignore
		this.storeReactions.push(autorun(() => {
			SessionStorageProvider.set(
				"betslip-accept-odds-change",
				JSON.stringify(this.acceptOddsChanges)
			);
		}));

		// prettier-ignore
		this.storeReactions.push(autorun(() => {
			SessionStorageProvider.set(
				"clicked-tips",
				JSON.stringify(this.betSlipState.clickedTips)
			);
		}));

		// prettier-ignore
		this.storeReactions.push(reaction(
			() =>
				this.betSlipState.actionController.actionInProgress ||
				this.betSlipState.isSubmitting,
			(value) => {
				runInAction(() => {
					if (value) {
						this.isSubmitDisabled = true;
					} else {
						this.isSubmitDisabled = false;
					}
				})
			}
		));

		// prettier-ignore
		this.storeReactions.push(reaction(
			() => ({
				isOneClick: this.betSlipState.betSlipBetType === BetSlipBetType.OneClickBet,
				isLoggedIn: this.userStore.isLoggedIn
			}),
			(arg) => {
				if (arg.isOneClick && arg.isLoggedIn) {
					SessionStorageProvider.set("ocb", "true")
				} else {
					SessionStorageProvider.remove("ocb")
				}
			}
		));
	}

	@action.bound
	private stopStoreReactions() {
		for (const disposer of this.storeReactions) {
			disposer();
		}
	}

	//#endregion sessionStorageSync

	//#region disposers

	@action.bound
	public onDispose() {
		this.betSlipState.onDispose();
		this.stopStoreReactions();
		clearInterval(this.countdownInterval);
	}

	@action.bound
	public onResetSlip() {
		this.betSlipState.reset({ resetSubmitDelay: false });
		if (!this.betSlipState.freebetId) {
			this.resetDraftFreebet();
		}
		this.rootStore.betBuilderStore.clearAdditionalOfferStores();

		runInAction(() => {
			this.cancelBetSlipReset();
			this.fullErrorMessage = "";
		});
	}

	@action.bound
	public resetBetSlipTypeToDefault() {
		this.betSlipState.onBetSlipBetTypeChange(BetSlipBetType.Normal);
	}

	//#endregion disposers
}
